import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { PrioShareDto, PrioShareFileDto } from '../../../../models/PrioShare';
import { ProjectId } from '../../../../models/Types';
import { usePublishPrioShare } from './hooks/usePublishPrioShare';
import { useUploadPrioShareFile } from './hooks/useUploadPrioShareFile';

interface PrioShareDraftContextType {
  draft: PrioShareDto | null;
  projectId: ProjectId;
  projectExtensionId: string;
  publishShare: ReturnType<typeof usePublishPrioShare>['mutateAsync'];
  isPublishing: boolean;
  uploadFile: (file: File, alternativeFilename?: string) => Promise<PrioShareFileDto | null>;
  isUploading: boolean;
  uploadProgress: number;
  isDraftReady: boolean;
}

const PrioShareDraftContext = createContext<
  PrioShareDraftContextType | undefined
>(undefined);

interface PrioShareDraftProviderProps {
  draft: PrioShareDto | null;
  projectId: ProjectId;
  projectExtensionId: string;
  children: React.ReactNode;
}

export const PrioShareDraftProvider: React.FC<PrioShareDraftProviderProps> = ({
  draft,
  projectId,
  projectExtensionId,
  children,
}) => {
  //#region ------------------------------ Defaults
  const { mutateAsync: publishShare, isLoading: isPublishing } =
    usePublishPrioShare();
  const { mutateAsync: uploadPrioShareFile, isLoading: isUploading } =
    useUploadPrioShareFile();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [uploadProgress, setUploadProgress] = useState(0);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const uploadFile = useCallback(
    async (file: File, alternativeFilename?: string) => {
      if (!draft) return null;
      setUploadProgress(0);
      const result = await uploadPrioShareFile({
        prioShareId: draft.prioShareId,
        projectExtensionId,
        file,
        alternativeFilename,
        onProgress: setUploadProgress,
      });
      setUploadProgress(0);
      return result.data ?? null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadPrioShareFile, draft?.prioShareId, projectExtensionId]
  );
  //#endregion

  const value = useMemo<PrioShareDraftContextType>(() => ({
    draft,
    projectId,
    projectExtensionId,
    publishShare,
    isPublishing,
    uploadFile,
    isUploading,
    uploadProgress,
    isDraftReady: !!draft,
  }), [draft, projectId, projectExtensionId, publishShare, isPublishing, uploadFile, isUploading, uploadProgress]);

  return (
    <PrioShareDraftContext.Provider value={value}>
      {children}
    </PrioShareDraftContext.Provider>
  );
};

export const usePrioShareDraft = () => {
  const context = useContext(PrioShareDraftContext);
  if (!context)
    throw new Error(
      'usePrioShareDraft must be used within PrioShareDraftProvider'
    );
  return context;
};
